import React, {
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
} from 'react';
import styled, { keyframes, css } from 'styled-components';
import {
  isIos,
  HealthMonitorCodes,
  SessionState,
} from '@biosensesignal/web-sdk';
import useDevice from '../hooks/useDevice';
import { useMediaPredicate } from 'react-media-hook';
import {
  useError,
  useLicenseKey,
  useMeasurementDuration,
  useVitalSignsKey,
  useMonitor,
  usePageVisibility,
  usePrevious,
  useWarning,
} from '../hooks';
import Stats from './Stats';
import StartButton from './StartButton';
import { mirror } from '../style/mirror';
import { Flex } from './shared/Flex';
import Timer from './Timer';
import media, { phone } from '../style/media';
import InfoBar from './InfoBar';
import { ErrorAlert, InfoAlert, WarningAlert } from './alert';

import Loader from './Loader';
import ScanTips from './ScanTips';
import DisclaimerModal from './DisclaimerModal';
import { VideoReadyState } from '../types';
import Mask from '../assets/mask.svg';
import { useNavigate } from 'react-router-dom';
import ClientService from '../services/ClientService';
import { T, useT, useSetLang, useLang } from "../Translation/index";
import { TranslationKeys, LangProps } from "../Translation/translation_types";
const MonitorWrapper = styled(Flex)<{ isSettingsOpen: boolean }>`
  flex-direction: column;
  width: 90%;
  justify-content: start;
  align-items: center;
  flex: 1;
  background-color: #f8f9fa;
  z-index: ${({ isSettingsOpen }) => isSettingsOpen && '-1'};
  ${media.tablet`
    width: fit-content;
    justify-content: center;
  `}
`;

const MeasurementContentWrapper = styled(Flex)<{ isMobile: boolean }>`
  position: relative;
  width: 100%;
  flex: 1;
  flex-direction: column;
  justify-content: center;
  align-items: center;
  overflow-y: auto;
  ${phone`
    padding: 0;
    gap: 8px;
  `}
`;

const VideoAndStatsWrapper = styled.div<{ isMobile: boolean }>`
  position: relative;
  flex-shrink: 0;
  width: 100%;
  aspect-ratio: 4 / 3;
  ${media.tablet`
    width: 650px;
  `}
  ${media.wide`
    width: 750px;
  `}
  ${phone`
    width: 100%;
    aspect-ratio: 3 / 4;
  `}
`;

const VideoWrapper = styled.div`
  position: absolute;
  inset: 0;
  overflow: hidden;
  border-radius: 15%;
`;

const Img = styled.img<{ isDesktop: boolean }>`
  position: absolute;
  top: 0;
  left: 0;
  width: 100%;
  height: 100%;
  z-index: 1;
  object-fit: ${({ isDesktop }) => (isDesktop ? 'contain' : 'cover')};
  pointer-events: none;
`;

const Video = styled.video`
  position: absolute;
  top: 0;
  left: 0;
  width: 100%;
  height: 100%;
  object-fit: cover;
  ${mirror};
`;

const ButtonWrapper = styled(Flex)<{ isMobile: boolean }>`
  z-index: 3;
  width: 100%;
  flex-direction: column;
  justify-content: start;
  align-items: center;
  margin-top: ${({ isMobile }) => isMobile ? '16px' : '-40px'};
  ${media.tablet`
    padding: 0;
    height: auto;
    width: auto;
    margin-top: -50px;
  `}
`;

const ButtomTimerWrapper = styled(Flex)`
  display: none;
  ${media.tablet`
    justify-content: center;
    align-items: center;
    margin-top: 10px;
    height: 30px;
    display: flex;
  `}
`;

const InfoBarWrapper = styled.div`
  height: 25px;
  width: 100%;
  display: flex;
  align-items: flex-end;
  ${media.mobile`
    flex: 0.45;
  `}
`;

// ✅ Centered action area BELOW the video
const CenterActionArea = styled.div`
  display: flex;
  justify-content: center;
  align-items: center;
  gap: 16px;
  width: 100%;
  max-width: 750px;
  margin-top: 16px;
  ${phone`
    max-width: 100%;
    padding: 0 8px;
  `}
`;

const ProgressBarWrapper = styled.div`
  width: 100%;
  height: 6px;
  background-color: #e0e0e0;
  border-radius: 3px;
  overflow: hidden;
`;

const ProgressBarFill = styled.div<{ progress: number; isMeasuring: boolean }>`
  height: 100%;
  width: ${({ progress }) => Math.min(progress, 100)}%;
  background: linear-gradient(90deg, #2196f3, #21cbf3);
  border-radius: 3px;
  transition: width 0.3s ease;
  ${({ isMeasuring }) =>
    isMeasuring &&
    css`
      animation: ${pulse} 2s infinite;
    `}
`;

const pulse = keyframes`
  0% { opacity: 0.7; }
  50% { opacity: 1; }
  100% { opacity: 0.7; }
`;

const AlertContainer = styled.div`
  position: absolute;
  top: 20px;
  left: 50%;
  transform: translateX(-50%);
  z-index: 5;
  width: 90%;
  max-width: 600px;
`;

const ErrorMessage = styled.div`
  color: #d80000;
  margin-top: 10px;
  text-align: center;
`;
const BinahMonitor = ({
  showMonitor,
  cameraId,
  onLicenseStatus,
}) => {
  if (!showMonitor) {
    return null;
  }

  const video = useRef<HTMLVideoElement>(null);
  const [isMeasurementEnabled, setIsMeasurementEnabled] = useState<boolean>(
    false,
  );
  const [startMeasuring, setStartMeasuring] = useState<boolean>(false);
  const [finishMeasuring, setfinishMeasuring] = useState<boolean>(false);
  const [isLoading, setIsLoading] = useState<boolean>(false);
  const [loadingTimeoutPromise, setLoadingTimeoutPromise] = useState<any>();
  const isPageVisible = usePageVisibility();
  const isMediaTablet = useMediaPredicate('(min-width: 1000px)');
  const [processingTime] = useMeasurementDuration();
  const [username] = useLicenseKey();
  const [licenseKey] = useLicenseKey();
  const {
    sessionState,
    vitalSigns,
    offlineMeasurements,
    error,
    warning,
    info,
  } = useMonitor(
    video,
    cameraId,
    processingTime,
    licenseKey,
    null,
    startMeasuring,
  );
  const prevSessionState = usePrevious(sessionState);
  const errorMessage = useError(error);
  const [msg, setMsg] = useState('');
  const [showDisclaimer, setShowDisclaimer] = useState(false);
  const [disclaimerAccepted, setDisclaimerAccepted] = useState(
    sessionStorage.getItem('disclaimerAccepted') === 'true'
  );
  const [wasMeasuring, setWasMeasuring] = useState(false);
  const [showStoppedMsg, setShowStoppedMsg] = useState(false);
  const warningMessage = useWarning(warning);
  const navigate = useNavigate();
  const isMeasuring = useCallback(
    () => sessionState === SessionState.MEASURING,
    [sessionState],
  );

  const isVideoReady = useCallback(
    () => video.current?.readyState === VideoReadyState.HAVE_ENOUGH_DATA,
    [],
  );

   // ✅ Compute progress percentage (0–100)
   const [progress, setProgress] = useState(0);
   useEffect(() => {
     let interval: NodeJS.Timeout | null = null;
     if (isMeasuring()) {
       setProgress(0);
       const totalMs = processingTime * 1000;
       const startTime = Date.now();
       interval = setInterval(() => {
         const elapsed = Date.now() - startTime;
         const pct = (elapsed / totalMs) * 100;
         if (pct >= 100) {
           setProgress(100);
           if (interval) clearInterval(interval);
         } else {
           setProgress(pct);
         }
       }, 100);
     } else {
       setProgress(0);
     }
     return () => {
       if (interval) clearInterval(interval);
     };
   }, [isMeasuring, processingTime]);

  const [vitalSignsKeyInLocalStorage,setVitalSignsKeyInLocalStorage,] = useVitalSignsKey();
    const [vitalSignsKey, setVitalSignsKey] = useState<string>(
      vitalSignsKeyInLocalStorage,
    );

  const startMeasurement = useCallback(() => {
    setIsLoading(true);
    setfinishMeasuring(false);
    sessionStorage.setItem('disclaimerAccepted', 'true');
    setDisclaimerAccepted(true);
    setShowDisclaimer(false);

    const clientService = ClientService.getInstance();
    const code = sessionStorage.getItem('code');

    (async () => {
      if(code){
        const msg2 = await clientService.handleProcess(code);
        if(isValidEmail(msg2)){
          if (sessionState === SessionState.ACTIVE) {
            setStartMeasuring(true);
            setLoadingTimeoutPromise(
              window.setTimeout(() => setIsLoading(true), processingTime * 1000),
            );
          } else if (isMeasuring()) {
            clearTimeout(loadingTimeoutPromise);
            setStartMeasuring(false);
          }
        }else{
          setIsLoading(false);
          setMsg(msg2);
        }
      }else {
        if (sessionState === SessionState.ACTIVE) {
          setStartMeasuring(true);
          setLoadingTimeoutPromise(
            window.setTimeout(() => setIsLoading(true), processingTime * 1000),
          );
        } else if (isMeasuring()) {
          clearTimeout(loadingTimeoutPromise);
          setStartMeasuring(false);
        }
      }
      
    })();

    
  }, [sessionState, setIsLoading, processingTime]);

  const handleButtonClick = useCallback(() => {
    if (disclaimerAccepted) {
      startMeasurement();
    } else {
      setShowDisclaimer(true);
    }
  }, [disclaimerAccepted, startMeasurement]);

  useEffect(() => {
    if (isMeasuring()) {
      setIsLoading(false);
      if (errorMessage) {
        setIsMeasurementEnabled(false);
      } else {
        setIsMeasurementEnabled(true);
      }
      !isPageVisible && setStartMeasuring(false);
    } else {
      // Measurement stopped — clear any pending loading timeout
      if (loadingTimeoutPromise) {
        clearTimeout(loadingTimeoutPromise);
      }
      setIsLoading(false);
    }
    if (
      (sessionState === SessionState.ACTIVE ||
        sessionState === SessionState.TERMINATED) &&
      errorMessage
    ) 
    {setIsMeasurementEnabled(false);}
    if (
      sessionState === SessionState.ACTIVE &&
      prevSessionState !== sessionState
    ) {
      setStartMeasuring(false);
      setIsLoading(false);
      //setVitalSign(vitalSigns);
      //setvitalSign(vitalSigns);
      if(finishMeasuring && vitalSigns.bloodPressure.value ){
        setVitalSignsKeyInLocalStorage(vitalSigns);
        setfinishMeasuring(false);
        console.log(vitalSigns.bloodPressure.value);
        navigate('/result');
      }
    }
    if (
      sessionState === SessionState.STOPPING &&
      prevSessionState !== sessionState
    ) {
      setfinishMeasuring(true);
    }
    console.log('isMobile '+mobile);
    console.log('isDesktop '+desktop);
    console.log('sessionState '+sessionState);
    console.log('prevSessionState '+(prevSessionState !== sessionState));
    console.log('vitalSigns '+!vitalSigns);
    console.log('bloodPressure '+vitalSigns.bloodPressure.value);
    console.log('isMeasuring '+isMeasuring());
    console.log('isMeasurementEnabled '+isMeasurementEnabled);
    console.log('StartMeasuring '+startMeasuring);
    console.log('IsLoading '+isLoading);
    console.log("-----------------");
  }, [errorMessage, sessionState, isPageVisible]);

  useEffect(() => {
    onLicenseStatus(!(error?.code in HealthMonitorCodes));
  }, [error]);

  useEffect(() => {
    const measuring = isMeasuring();
    if (measuring) {
      setWasMeasuring(true);
      setShowStoppedMsg(false);
    } else if (wasMeasuring && !measuring) {
      setShowStoppedMsg(true);
      setWasMeasuring(false);
    }
  }, [sessionState]);

  const { isMobile: mobile, isDesktop: desktop } = useDevice();
  const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
  function isValidEmail(email) {
    return emailRegex.test(email);
  }
  return (
    <>
      <MonitorWrapper isSettingsOpen={false}>
        {msg && <ErrorMessage>{msg}</ErrorMessage>}
        {desktop && isMeasurementEnabled && <ButtomTimerWrapper>
            {isMeasurementEnabled && (
              <Timer started={isMeasuring()} durationSeconds={processingTime} />
            )}
          </ButtomTimerWrapper>
          }
        <MeasurementContentWrapper isMobile={mobile}>
          <ErrorAlert
            message={errorMessage ? <T id={errorMessage as LangProps} /> : undefined}
            stoppedMessage={showStoppedMsg ? <T id="measurement_stopped" /> : undefined}
          />
          <InfoBarWrapper>
            <InfoBar
              showTimer={isMeasurementEnabled && !isMediaTablet}
              isMeasuring={isMeasuring()}
              durationSeconds={processingTime}
              offlineMeasurements={offlineMeasurements}
            />
          </InfoBarWrapper>
          <VideoAndStatsWrapper isMobile={mobile}>
            <VideoWrapper>
              <Img src={Mask} isDesktop={desktop} />
              <Video
                ref={video}
                id="video"
                muted={true}
                playsInline={true}
              />
            </VideoWrapper>
            {isMeasuring() && <WarningAlert message={warningMessage} />}
            {isMeasuring() && <InfoAlert message={info.message} msg={<T id={info.message as LangProps}/>} des={<T id={info.des as LangProps}/>} />}
            {/* {isMeasuring() && <InfoAlert message={info.message}/>} */}
            {!isVideoReady() && licenseKey && <Loader />}
          </VideoAndStatsWrapper>
          {!desktop && <ButtomTimerWrapper>
            {isMeasurementEnabled && (
              <Timer started={isMeasuring()} durationSeconds={processingTime} />
            )}
          </ButtomTimerWrapper>
          }
          <ButtonWrapper isMobile={mobile}>
          {isMeasuring() && !errorMessage && !warningMessage && isMeasurementEnabled && (
              <Stats vitalSigns={vitalSigns} />
          )}
          {isMeasuring() && !errorMessage && !warningMessage && isMeasurementEnabled ? (
              <CenterActionArea>
                <ProgressBarWrapper>
                  <ProgressBarFill progress={progress} isMeasuring={isMeasuring()} />
                </ProgressBarWrapper>
              </CenterActionArea>
            ) : (
              (!errorMessage || errorMessage === 'ERROR_3003') && (
                <StartButton
                  isLoading={isLoading}
                  isMeasuring={isMeasuring()}
                  onClick={handleButtonClick}
                />
              )
            )}
            <ScanTips active={isMeasuring()} showIdle={isVideoReady() && !isMeasuring()} />
          </ButtonWrapper>
        </MeasurementContentWrapper>
      </MonitorWrapper>
      {showDisclaimer && (
        <DisclaimerModal
          onAccept={startMeasurement}
          onBack={() => setShowDisclaimer(false)}
        />
      )}
    </>
  );
};

export default BinahMonitor;
